import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Target, Lightbulb, Shield, Zap, Eye, Heart, ArrowRight, CheckCircle, Clock, Users, MessageCircle, CreditCard, Building2, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const differentiators = [
    {
      icon: Clock,
      title: '7-Day Auto-Approval',
      description: 'Invoices automatically approved after 7 days, eliminating approval bottlenecks and speeding up payments.'
    },
    {
      icon: MessageCircle,
      title: 'Built-in Customer Chat',
      description: 'Communicate directly with customers about invoices, resolve disputes, and build stronger relationships.'
    },
    {
      icon: Zap,
      title: 'Scheduled Payments',
      description: 'Automatically collect payments on due dates, ensuring consistent cash flow without manual follow-up.'
    },
    {
      icon: CreditCard,
      title: 'Guest Invoice Portal',
      description: 'Send invoices to anyone with a simple link - no account required for your customers to view and pay.'
    }
  ];

  const visionGoals = [
    {
      icon: Heart,
      title: 'Freedom from Payment Stress',
      description: 'We want every business owner to sleep better at night knowing their invoices will be paid on time — no anxiety, no constant chasing, no wondering if the money will arrive.'
    },
    {
      icon: Clock,
      title: 'More Time for What Truly Matters',
      description: 'By removing the headache of credit control, Receevit® gives business owners their time back. Time to grow their business, spend with family, or focus on what they love instead of chasing overdue payments.'
    },
    {
      icon: Shield,
      title: 'A Fairer Business World',
      description: 'Our vision is to create a culture where paying on time is the norm, not the exception. By motivating early payments and improving transparency, we make B2B trade fairer and more respectful for everyone.'
    }
  ];

  const trustFeatures = [
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'All payments processed securely through Stripe with enterprise-grade encryption.'
    },
    {
      icon: CheckCircle,
      title: 'GDPR Compliant',
      description: 'Full compliance with data protection regulations and privacy standards.'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Get help when you need it with our responsive customer support team.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Transforming B2B Credit Control | Receevit®</title>
        <meta name="description" content="Learn about Receevit®'s mission to help businesses reduce late payments and improve cash flow through intelligent automation and smart insights." />
        <link rel="canonical" href="https://www.receevit.com/about" />
        <meta property="og:title" content="About Us - Transforming B2B Credit Control | Receevit®" />
        <meta property="og:description" content="Learn about Receevit®'s mission to help businesses reduce late payments and improve cash flow through intelligent automation and smart insights." />
        <meta property="og:url" content="https://www.receevit.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "About Us - Transforming B2B Credit Control | Receevit®",
            "url": "https://www.receevit.com/about",
            "description": "Learn about Receevit®'s mission to help businesses reduce late payments and improve cash flow through intelligent automation and smart insights.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "About",
                "item": "https://www.receevit.com/about"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="pt-16">
        {/* Mission Statement */}
        <section className="relative overflow-hidden bg-[#1F11ED] py-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold text-white mb-6"
          >
            About{' '}
            <span className="bg-gradient-to-r from-blue-300 to-white bg-clip-text text-transparent">
              Receevit®
            </span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto"
          >
            Receevit® was created to help businesses stop wasting time chasing invoices and start focusing on growth. By automating approvals, scheduling payments, and streamlining communication with clients, Receevit® removes the stress of credit control. Our platform gives businesses the confidence of predictable cash flow, faster payments, and more time to focus on what truly matters — growing their business and building stronger client relationships.
          </motion.p>
        </div>
      </section>

      {/* The Story / Origin */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">The Story Behind Receevit®</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              We built Receevit® because we experienced the same pain points you face every day.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">The Pain We Saw</h3>
                <p className="text-blue-100 mb-6">
                  Late payments, poor credit control tools, and endless hours spent chasing invoices. 
                  We watched businesses struggle with cash flow while drowning in administrative work.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-blue-100">30+ days average payment delays</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-blue-100">Hours wasted on manual follow-ups</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-blue-100">Poor visibility into payment status</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">How Receevit® Solves It</h3>
                <p className="text-blue-100 mb-6">
                  We created a platform that automates the entire invoice lifecycle, 
                  from creation to payment collection, with built-in communication tools.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-blue-100">Automatic payment collection</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-blue-100">Real-time customer communication</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-blue-100">Complete payment visibility</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">What Makes Us Different</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              Receevit® is more than just an invoicing tool — it's credit control reimagined for modern B2B businesses.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center shadow-2xl hover:shadow-3xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-[#1F11ED]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-blue-100">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision for the Future */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Vision for the Future</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              Our vision goes beyond technology — we're building a future where business owners can focus on what truly matters.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {visionGoals.map((goal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <goal.icon className="w-8 h-8 text-[#1F11ED]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{goal.title}</h3>
                <p className="text-blue-100">{goal.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Transparency */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Trust & Transparency</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              Your business data and payments are protected by enterprise-grade security and compliance standards.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center shadow-2xl hover:shadow-3xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-[#1F11ED]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-blue-100">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Human Touch */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">The Human Touch</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              Behind every feature is a commitment to making B2B payments simple, transparent, and human.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Why I Started Receevit®</h3>
                <p className="text-blue-100 mb-6">
                  "After years of watching businesses struggle with cash flow and spending countless hours on manual invoice management, 
                  I knew there had to be a better way. Receevit® was born from the belief that technology should solve real problems, 
                  not create more complexity."
                </p>
                <div className="text-right">
                  <p className="text-white font-semibold">— Mr V. Vincent, Founder & CEO</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Our Values</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Target className="w-5 h-5 text-blue-300 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-white">Innovation</p>
                      <p className="text-blue-100 text-sm">Constantly improving to solve real business problems</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-blue-300 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-white">Trust</p>
                      <p className="text-blue-100 text-sm">Building reliable, secure solutions you can depend on</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Zap className="w-5 h-5 text-blue-300 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-white">Simplicity</p>
                      <p className="text-blue-100 text-sm">Making B2B payments as simple as possible</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              We're Just Getting Started
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join us on the journey to reshape how businesses get paid. 
              Experience the future of B2B invoicing with our 30-day free trial.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center bg-white text-[#1F11ED] px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Get Started with Receevit®
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
      </div>
    </>
  );
};

export default AboutPage;